import { useRef, useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { texts } from "../utils/texts/texts";

interface ConfirmModalOptions {
    requireTypedConfirmation?: string;
}

const useConfirmModal = (msgText: string, options: ConfirmModalOptions = {}): [() => JSX.Element, Function] => {
    const [callbacks, setCallbacks] = useState<Function[]>([]);
    const [typedConfirmation, setTypedConfirmation] = useState('');

    const requiredText = options.requireTypedConfirmation;
    const canConfirm = !requiredText || typedConfirmation === requiredText;

    // Latest render's values, read by the stable ConfirmModal component below via ref so its
    // own identity never changes across renders — recreating it on every keystroke (from
    // typedConfirmation changing) would make React unmount/remount the Modal, causing it to
    // flicker closed and reopen on every character typed.
    const latest = useRef({ msgText, requiredText, callbacks, typedConfirmation, canConfirm });
    latest.current = { msgText, requiredText, callbacks, typedConfirmation, canConfirm };

    const trigger = (callback: Function) => {
        setTypedConfirmation('');
        setCallbacks([callback]);
    };
    const handleConfirm = async () => {
        const [callback] = latest.current.callbacks;
        setCallbacks([]);
        await callback?.();
    }

    const [ConfirmModal] = useState(() => () => {
        const { msgText, requiredText, callbacks, typedConfirmation, canConfirm } = latest.current;

        return <Modal show={!!callbacks.length} onHide={() => setCallbacks([])} className="modal fade">
            <Modal.Header closeButton>
                <div className="center gap-2">

                    <div className='center bg-danger-lighter p-0 rounded-circle'>
                        <div className='bg-danger-light p-0 rounded-circle' >
                            <i className="bi bi-exclamation-circle text-danger h4 m-0" />
                        </div>
                    </div>
                    <Modal.Title className="">{texts.confirmation}</Modal.Title>
                </div>
            </Modal.Header>
            <Modal.Body className="d-flex flex-column gap-4">
                <div>{msgText}</div>
                <div>{texts.thisActionCannotBeUndone}</div>
                {requiredText && (
                    <Form.Group>
                        <Form.Label className="small text-muted">{texts.typeNameToConfirm}</Form.Label>
                        <div className="fw-semibold mb-2">{requiredText}</div>
                        <Form.Control
                            autoFocus
                            value={typedConfirmation}
                            onChange={(e) => setTypedConfirmation(e.target.value)}
                        />
                    </Form.Group>
                )}
                <div className='d-flex justify-content-between gap-2'>
                    <button className='btn btn-outline-secondary flex-1' onClick={() => setCallbacks([])}>{texts.cancel}</button>
                    <button className='btn btn-danger flex-1' onClick={handleConfirm} disabled={!canConfirm}>{texts.confirm}</button>
                </div>
            </Modal.Body>
        </Modal>;
    });

    return [ConfirmModal, trigger]
}

export default useConfirmModal;
